import React, { Component } from 'react';
import './HouseForm.css';
import posthog from "posthog-js";
import StateConstants from "src/states/StateContstants"
import AddressStreetView from "src/components/address_images/AddressStreetView"
import ApiHandler from "src/api/ApiHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ValuationHistorySection from './ValuationHistorySection';
import LoginHelper from "src/components/login/LoginHelper"
import SaveValuationButton from "src/components/general/SaveValuationButton"
import Loader from "src/components/general/Loading"
import FeatureProvider from "src/features/FeatureProvider"
import FeatureFlagIds from "src/features/FeatureFlagIds"
import navigationService from "src/navigation/NavigationService"
import HouseInformationHelper from "src/utils/HouseInformationHelper"
import UserFeatureHelper from "src/utils/UserFeatureHelper"
import PropertyFeatures from "src/components/general/PropertyFeatures"
import PriceChangePills from "./PriceChangePills"
import SaleTimeline from "./SaleTimeline"

interface HouseFormProps {
    address: string;
    displayAddress: string;
    stateHandler: EventAdaptingStateHandler;
    apiHandler: ApiHandler;
    loginHelper: LoginHelper;
    loader: Loader;
    featureProvider: FeatureProvider;
}

interface HouseFormState {
    showChart: boolean;
    isMobile: boolean;
    valuationLoading: boolean;
    showRefineModal: boolean;
}

class HouseForm extends Component<HouseFormProps, HouseFormState> {
    IS_TIME_SERIES_ENABLED = true;

    constructor(props: HouseFormProps) {
        super(props);
        this.state = {
            showChart: false,
            isMobile: false,
            valuationLoading: false,
            showRefineModal: false,
        };
        this.props.stateHandler.addListener(StateConstants.SHOW_TIME_SERIES, (state: any) => {
            this.setState({
                showChart: state
            });
        });
        this.props.stateHandler.addListener(StateConstants.IS_MOBILE, (state: any) => {
            this.setState({
                isMobile: state
            });
        });
        this.props.stateHandler.addListener(StateConstants.VALUATION_LOADING, (state: any) => {
            this.setState({
                valuationLoading: state
            });
        });
        this.props.stateHandler.addListener(StateConstants.USER_FEATURES, () => {
            this.setState({});
        });
    }

    toggleChart = () => {
        this.props.stateHandler.set(StateConstants.SHOW_TIME_SERIES, !this.props.stateHandler.get(StateConstants.SHOW_TIME_SERIES));
    };

    formatCurrency = (amount: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };


    render() {
        const { address, apiHandler } = this.props;
        
        const valuation = this.props.stateHandler.get(StateConstants.VALUATION_STATE);
        const currentValuation = valuation ? valuation["valuation"] || 0 : 0;
        const valuationLower = valuation ? valuation["valuation_lower"] : null;
        const valuationUpper = valuation ? valuation["valuation_upper"] : null;
        const timeSeries = valuation ? valuation["vts"] : null;
        const showTimeSeries = this.props.stateHandler.get(StateConstants.SHOW_TIME_SERIES);
        const isMobile = this.props.stateHandler.get(StateConstants.IS_MOBILE);

        const oneDayChange = HouseInformationHelper.calculatePriceChange(timeSeries, HouseInformationHelper.getOneDayAgoDate());
        const oneMonthChange = HouseInformationHelper.calculatePriceChange(timeSeries, HouseInformationHelper.getOneMonthAgoDate());
        const oneYearChange = HouseInformationHelper.calculatePriceChange(timeSeries, HouseInformationHelper.getOneYearAgoDate());

        const featuresLoading = this.props.stateHandler.get(StateConstants.USER_FEATURES) === undefined;
        const isGated = !UserFeatureHelper.hasFeature(this.props.stateHandler, UserFeatureHelper.DETAILED_VALUATIONS);
        const roundToNearest = (value: number, nearest: number) => Math.round(value / nearest) * nearest;
        const displayLower = isGated && valuationLower ? roundToNearest(valuationLower, 50000) : valuationLower;
        const displayUpper = isGated && valuationUpper ? roundToNearest(valuationUpper, 50000) : valuationUpper;
        let estimatedValuation = this.formatCurrency(currentValuation);

        return (
            <><div className="house-form-container">
                <div className="house-form-content">
                    {/* Address Section */}
                    <div className="form-section">
                        <div className="address-display">
                            <span className="address-icon">📍</span>
                            <div className="address-title-group">
                                {(() => {
                                    const commaIndex = address.indexOf(',');
                                    if (commaIndex === -1) return <h1 className="address-heading">{address}</h1>;
                                    const firstLine = address.substring(0, commaIndex);
                                    const secondLine = address.substring(commaIndex + 1).trim();
                                    return (
                                        <>
                                            <h1 className="address-heading">{firstLine}</h1>
                                            <span className="address-subtitle">{secondLine}</span>
                                        </>
                                    );
                                })()}
                            </div>
                            <div className="save-button-corner">
                                <SaveValuationButton
                                    valuationId={valuation.id}
                                    apiHandler={apiHandler}
                                    stateHandler={this.props.stateHandler}
                                    loginHelper={this.props.loginHelper}
                                    isSingleValuationPage={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Street View Section */}
                    <div className="street-view-section">
                        <AddressStreetView
                            address={address}
                            locationId={valuation?.id}
                            stateHandler={this.props.stateHandler}
                            apiHandler={apiHandler}
                            width={480}
                            height={200}
                        />
                    </div>

                    {/* Property Features */}
                    <PropertyFeatures features={valuation?.af} />
                    <PriceChangePills
                        oneYearChange={oneYearChange}
                        oneMonthChange={oneMonthChange}
                        oneDayChange={oneDayChange}
                    />
                    <br></br>
                    <br></br>


                    {/* Valuation Section */}
                    <div className="valuation-section-container">
                        <div className="valuation-section">
                            {this.state.valuationLoading || featuresLoading ?
                                this.props.loader.render("", "small") :
                                <>
                                    <div className="valuation-range-display">
                                        {displayLower && (
                                            <div className="valuation-bound valuation-bound-lower">
                                                <span className="valuation-bound-value">{this.formatCurrency(displayLower)}</span>
                                                <span className="valuation-bound-label">Low</span>
                                            </div>
                                        )}
                                        <div className={`valuation-info valuation-value-container${isGated ? ' valuation-range-blurred' : ''}`}>
                                            <span id="house-form-valuation-value" className="valuation-amount">
                                                {isGated ? '€●●●,●●●' : estimatedValuation}
                                            </span>
                                            <span className="valuation-label">{isGated ? 'Exact estimate locked' : 'Estimate'}</span>
                                        </div>
                                        {displayUpper && (
                                            <div className="valuation-bound valuation-bound-upper">
                                                <span className="valuation-bound-value">{this.formatCurrency(displayUpper)}</span>
                                                <span className="valuation-bound-label">High</span>
                                            </div>
                                        )}
                                    </div>
                                    {isGated && (
                                        <div className="valuation-limit-overlay">
                                            <p className="valuation-range-approx-note">
                                                The price range is rounded to the nearest €50k · Unlock the exact values below
                                            </p>
                                            <button
                                                className="valuation-limit-cta"
                                                onClick={() => {
                                                    posthog?.capture("plans_opened", { source: "unlock_valuation" })
                                                    this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, true)
                                                }}
                                            >
                                                Unlock this valuation
                                            </button>
                                        </div>
                                    )}
                                </>
                            }
                        </div>
                        {this.props.featureProvider.getFeatureFlag(FeatureFlagIds.ENABLE_SALE_ENQUIRIES).isEnabled() && (
                        <div className="valuation-actions-section">
                            <div className="valuation-actions">
                                <button
                                    className="sell-home-button"
                                    onClick={() => navigationService.navigateToSellMyHome(address)}
                                >
                                    SELL
                                </button>
                                <button
                                    className="sell-home-button"
                                    onClick={() => navigationService.navigateToGetValuation(address)}
                                >
                                    VALUE
                                </button>
                            </div>
                        </div>
                        )}
                    </div>
                    <br></br>
                    <SaleTimeline valuation={valuation} />
                    <br></br>

                    {!isMobile && timeSeries && (
                        <ValuationHistorySection
                            timeSeries={timeSeries}
                            stateHandler={this.props.stateHandler}
                            apiHandler={apiHandler}
                        />
                    )}

                </div>
            </div>
                {this.state.showRefineModal && (
                    <>
                        <div
                            className="refine-modal-backdrop"
                            onClick={() => this.setState({ showRefineModal: false })}
                        />
                        <div className="refine-modal">
                            <button
                                className="refine-modal-close"
                                onClick={() => this.setState({ showRefineModal: false })}
                            >
                                &#10005;
                            </button>
                            <div className="refine-modal-icon">🏡</div>
                            <h3 className="refine-modal-title">Tailored Valuations — Coming Soon</h3>
                            <p className="refine-modal-message">
                                Valuations tailored to the nuances and features of your home. Watch this space.
                            </p>
                        </div>
                    </>
                )}
            </>
        );
    }
}

export default HouseForm;
